import { Inject, Injectable, Optional } from '@angular/core';
import {
  DocumentRefService,
  ParamsService,
  WindowRefService,
} from '@callrail/looky/util';
import { from, tap, Observable, ReplaySubject, catchError } from 'rxjs';
import { ErrorReporterService } from '../error-reporter/error-reporter.service';
import { Router } from '@angular/router';

declare const SM: any; // semrush js sdk

type ClientMethod =
  | 'getAccessToken'
  | 'pushUrl'
  | 'replaceUrl'
  | 'requestEmailReports'
  | 'requestInAppPurchase'
  | 'showLoginModal';

interface SemrushSdk {
  client: (methodName: ClientMethod, ...params) => Promise<string>;
  init: () => Promise<unknown>;
  addCallback: (
    callbackName: string,
    callback: (newUrl: string, options?: { state: any }) => unknown
  ) => () => void;
}

@Injectable({
  providedIn: 'root',
})
export class SemrushIframeService {
  private semrushToken = new ReplaySubject<string>(1);
  public readonly semrushToken$ = this.semrushToken.asObservable();

  constructor(
    private documentRefService: DocumentRefService,
    private windowRefService: WindowRefService,
    private errorReporterService: ErrorReporterService,
    private router: Router,
    private paramsService: ParamsService,
    @Optional() @Inject('sm') private sm?: SemrushSdk
  ) {
    if (this.windowRefService.inSemrushIframe()) {
      if (this.windowRefService.testingIframe()) {
        this.semrushToken.next('test-token');
      } else {
        this.appendScriptToBody();
      }
    }
  }

  private get sdk(): SemrushSdk {
    return this.sm || SM;
  }

  public refreshToken(): Observable<string> {
    return from(this.sdk.client('getAccessToken')).pipe(
      tap((accessToken) => {
        this.semrushToken.next(accessToken);
      }),
      catchError(() => {
        this.semrushToken.error('Could not refresh token.');
        return from(this.sdk.client('showLoginModal'));
      })
    );
  }

  public updateIframeUrl(url: string, replace = false): void {
    if (replace) {
      this.sdk.client('replaceUrl', url);
    } else {
      this.sdk.client('pushUrl', url);
    }
  }

  public onUrlPop(callback: (newUrl: string) => void): () => void {
    return this.sdk.addCallback('onUrlPop', callback);
  }

  public promptSemrushEmailOptIn(): void {
    this.sdk.client('requestEmailReports');
  }

  public requestInAppPurchase(productIds: string[]): void {
    this.sdk.client('requestInAppPurchase', productIds);
  }

  public showLoginModal(): void {
    this.sdk.client('showLoginModal');
  }

  public updateSemrushUrl(url: string, agencyId: string, iframeParam: string) {
    const navigation = this.router.getCurrentNavigation();
    const navigatingBack =
      navigation?.trigger === 'popstate' ||
      navigation?.extras.state?.navigatingBack;
    if (navigatingBack) {
      return; // don't update semrush iframe url if we're navigating back.
    }
    const currentParams = this.paramsService.fromString(url);
    if (currentParams.cr_id && currentParams[iframeParam]) {
      return;
    }
    const updatedParams = {
      ...currentParams,
      cr_id: agencyId,
      [iframeParam]: true,
    };
    const baseUrl = url.split('?')[0];
    url = this.paramsService.appendToString(baseUrl, updatedParams);
    this.updateIframeUrl(url);
  }

  private appendScriptToBody(): void {
    this.windowRefService.nativeWindow().onSmSdkLoad = () => {
      this.sdk
        .init()
        .then(() => this.sdk.client('getAccessToken'))
        .then((accessToken) => {
          this.semrushToken.next(accessToken);
        })
        .catch(() => {
          this.semrushToken.error('Could not retrieve access token.');
        });
    };
    const script = this.documentRefService
      .nativeDocument()
      .createElement('script');
    // this file was downloaded directly from https://static.semrush.com/app-center/sdk.js
    // and then minified with an online minifier https://www.toptal.com/developers/javascript-minifier/
    // and then uploaded to our cdn following these instructions https://callrail.atlassian.net/l/c/SvRVYswq
    script.src =
      'https://static.semrush.com/developer-app-center/static/sdk_0.0.7.js';
    script.type = 'text/javascript';
    this.documentRefService.nativeDocument().body.appendChild(script);
  }

  private handleSdkError(error): Error {
    const initialCode = error.status;
    error.status = 500; // ensure we _do_ actually badger this no matter what code it had initially
    const metadata = { uncaught: true, code: initialCode };
    this.errorReporterService.report(this.errorWithName(error), metadata);
    return error;
  }

  private errorWithName(error: Error): Error {
    // for easier scanning in Honeybadger UI and team/individual assignment
    error.name = `SemrushIframeService::${error.name}`;
    return error;
  }
}
